import React from 'react';
import { ArrowRight, Shirt, Gift, Umbrella } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Reveal, Stagger, StaggerItem } from '../motion/Reveal';

const catalogs = [
  {
    icon: Shirt,
    iconBg: 'from-cyan-400 to-purple-500',
    check: 'text-cyan-400',
    href: 'https://www.textile-world.eu/?lang=en&customer=tw1',
    title: { sl: 'PROMOCIJSKA OBLAČILA', en: 'PROMOTIONAL CLOTHING', it: 'ABBIGLIAMENTO PROMOZIONALE' },
    paragraphs: [
      {
        sl: 'Poskrbite za večjo prepoznavnost vaše blagovne znamke z vrhunskimi promocijskimi oblačili. Izbirajte med majicami, kapami, puloverji, flisi, jaknami in drugimi izdelki priznanih blagovnih znamk, kot so TeeJays, B&C, Fruit of the Loom, James & Nicholson, Stedman, Sol\'s in Craft.',
        en: 'Increase your brand visibility with premium promotional clothing. Choose from t-shirts, caps, sweaters, fleeces, jackets and other products from renowned brands like TeeJays, B&C, Fruit of the Loom, James & Nicholson, Stedman, Sol\'s and Craft.',
        it: 'Aumenta la visibilità del tuo marchio con abbigliamento promozionale di alta qualità. Scegli tra t-shirt, cappelli, maglioni, pile, giacche e altri prodotti di marchi rinomati come TeeJays, B&C, Fruit of the Loom, James & Nicholson, Stedman, Sol\'s e Craft.'
      },
      {
        sl: 'Ponujamo popolno kombinacijo kakovosti, sodobnega dizajna in konkurenčnih cen – idealno za promocije, dogodke, ekipe ali delovna okolja.',
        en: 'We offer the perfect combination of quality, modern design and competitive prices – ideal for promotions, events, teams or work environments.',
        it: 'Offriamo la combinazione perfetta di qualità, design moderno e prezzi competitivi – ideale per promozioni, eventi, team o ambienti di lavoro.'
      }
    ],
    whyTitle: { sl: 'Zakaj izbrati našo ponudbo?', en: 'Why choose our offer?', it: 'Perché scegliere la nostra offerta?' },
    bullets: [
      { sl: 'Širok izbor priznanih znamk na enem mestu', en: 'Wide selection of renowned brands in one place', it: 'Ampia selezione di marchi rinomati in un unico posto' },
      { sl: 'Atraktivni kroji in moderne barve', en: 'Attractive cuts and modern colors', it: 'Tagli attraenti e colori moderni' },
      { sl: 'Vrhunska kakovost in udobje nošenja', en: 'Premium quality and wearing comfort', it: 'Qualità premium e comfort di utilizzo' },
      { sl: 'Personalizacija s tiskom ali vezenjem po meri', en: 'Customization with printing or embroidery', it: 'Personalizzazione con stampa o ricamo' }
    ]
  },
  {
    icon: Gift,
    iconBg: 'from-yellow-400 to-red-500',
    check: 'text-yellow-400',
    href: 'https://generalcatalogue2026.eu/40edbe98075cfdadfa3399d65bd57834/#',
    title: { sl: 'POSLOVNA IN PROMOCIJSKA DARILA', en: 'BUSINESS & PROMOTIONAL GIFTS', it: 'REGALI AZIENDALI E PROMOZIONALI' },
    paragraphs: [
      {
        sl: 'Izstopajte z izbranimi promocijskimi izdelki, ki učinkovito predstavljajo vašo blagovno znamko. Ponujamo širok nabor cenovno dostopnih in privlačnih izdelkov – od pisal, obeskov in zložljivih vrečk do izdelkov za otroke in drugih praktičnih rešitev za vse priložnosti.',
        en: 'Stand out with selected promotional products that effectively represent your brand. We offer a wide range of affordable and attractive products – from pens, keychains and foldable bags to children\'s items and other practical solutions for all occasions.',
        it: 'Distinguiti con prodotti promozionali selezionati che rappresentano efficacemente il tuo marchio. Offriamo un\'ampia gamma di prodotti convenienti e attraenti – dalle penne, portachiavi e borse pieghevoli agli articoli per bambini e altre soluzioni pratiche per tutte le occasioni.'
      },
      {
        sl: 'Z našimi darili ustvarite trajen vtis pri strankah, poslovnih partnerjih ali na dogodkih.',
        en: 'Create a lasting impression with our gifts for customers, business partners or at events.',
        it: 'Crea un\'impressione duratura con i nostri regali per clienti, partner commerciali o eventi.'
      }
    ],
    whyTitle: { sl: 'Zakaj izbrati našo ponudbo?', en: 'Why choose our offer?', it: 'Perché scegliere la nostra offerta?' },
    bullets: [
      { sl: 'Velik izbor atraktivnih izdelkov in barv', en: 'Wide selection of attractive products and colors', it: 'Ampia selezione di prodotti e colori attraenti' },
      { sl: 'Konkurenčne cene za vse količine', en: 'Competitive prices for all quantities', it: 'Prezzi competitivi per tutte le quantità' },
      { sl: 'Hitra in zanesljiva dobava', en: 'Fast and reliable delivery', it: 'Consegna rapida e affidabile' },
      { sl: 'Možnost personalizacije z vašim logotipom', en: 'Customization with your logo', it: 'Personalizzazione con il vostro logo' }
    ]
  },
  {
    icon: Umbrella,
    iconBg: 'from-green-400 to-blue-500',
    check: 'text-green-400',
    href: 'https://view.publitas.com/md-cee_en/25-1-yourchoice_eng_vr-edlgx9_63pq9/page/1',
    title: { sl: 'PROMOCIJSKI IZDELKI PO VAŠI MERI', en: 'CUSTOM PROMOTIONAL PRODUCTS', it: 'PRODOTTI PROMOZIONALI PERSONALIZZATI' },
    paragraphs: [
      {
        sl: 'Ustvarite promocijske izdelke, ki popolnoma odražajo identiteto vaše blagovne znamke. Izdelujemo produkte po meri – v vaših barvah, z vašim dizajnom in popolnoma prilagojene vašim potrebam. Izbirate lahko med dežniki, vrečkami, kapami, nogavicami in drugimi promocijskimi rešitvami.',
        en: 'Create promotional products that perfectly reflect your brand identity. We manufacture custom products – in your colors, with your design and fully adapted to your needs. Choose from umbrellas, bags, caps, socks and other promotional solutions.',
        it: 'Crea prodotti promozionali che riflettono perfettamente l\'identità del tuo marchio. Produciamo prodotti personalizzati – nei tuoi colori, con il tuo design e completamente adattati alle tue esigenze. Scegli tra ombrelli, borse, cappelli, calzini e altre soluzioni promozionali.'
      },
      {
        sl: 'Poskrbimo za profesionalno izvedbo od ideje do končnega izdelka.',
        en: 'We ensure professional execution from idea to final product.',
        it: 'Garantiamo un\'esecuzione professionale dall\'idea al prodotto finale.'
      }
    ],
    whyTitle: { sl: 'Zakaj izbrati izdelke po meri?', en: 'Why choose custom products?', it: 'Perché scegliere prodotti personalizzati?' },
    bullets: [
      { sl: 'Širok izbor izdelkov z možnostjo popolne prilagoditve', en: 'Wide selection of products with full customization options', it: 'Ampia selezione di prodotti con opzioni di personalizzazione completa' },
      { sl: 'Kratek dobavni rok', en: 'Short delivery time', it: 'Tempi di consegna brevi' },
      { sl: 'Preverjena kakovost materialov in izdelave', en: 'Proven quality of materials and workmanship', it: 'Qualità comprovata dei materiali e della lavorazione' },
      { sl: 'Možnost celostne grafične priprave', en: 'Complete graphic design services available', it: 'Servizi di progettazione grafica completa disponibili' }
    ]
  }
];

const CatalogCard = ({ catalog }) => {
  const { t } = useLanguage();
  const Icon = catalog.icon;
  return (
    <Card className="team-card bg-[#1a1a1a] border-[rgba(255,255,255,0.1)] overflow-hidden group hover:border-cyan-500/50 h-full">
      <CardHeader className="pb-4">
        <div className={`w-14 h-14 rounded-full bg-gradient-to-r ${catalog.iconBg} flex items-center justify-center mb-4`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
        <CardTitle className="text-[white] text-xl mb-2">{t(catalog.title)}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {catalog.paragraphs.map((p) => (
          <p key={p.en} className="text-[#b0b0b0] body-small">{t(p)}</p>
        ))}
        <div className="space-y-2 pt-2">
          <p className="text-[white] font-semibold body-small">{t(catalog.whyTitle)}</p>
          <ul className="space-y-1">
            {catalog.bullets.map((b) => (
              <li key={b.en} className="flex items-start gap-2 text-[#b0b0b0] body-small">
                <span className={catalog.check}>✔</span>
                {t(b)}
              </li>
            ))}
          </ul>
        </div>
        <div className="pt-4">
          <a href={catalog.href} target="_blank" rel="noopener noreferrer">
            <Button className="btn-primary w-full">
              {t({ sl: 'OGLED KATALOGA', en: 'VIEW CATALOG', it: 'VISUALIZZA CATALOGO' })}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

export const Catalogs = () => {
  const { t } = useLanguage();
  return (
    <section className="py-24 bg-[#0a0a0a]">
      <div className="container mx-auto px-5 md:px-10">
        <Reveal className="text-center mb-16">
          <h2 className="heading-4 mb-6 gradient-text-primary">
            {t({ sl: 'VEČ KOT LE TISK', en: 'MORE THAN JUST PRINT', it: 'PIÙ CHE SOLO STAMPA' })}
          </h2>
          <p className="body-large text-[#b0b0b0] max-w-3xl mx-auto">
            {t({
              sl: 'Poleg tiska vam nudimo tudi promocijski tekstil, delovna oblačila in obutev ter poslovna darila – vse za profesionalno in prepoznavno podobo vašega podjetja. Oglejte si naše spletne kataloge.',
              en: 'In addition to printing, we also offer promotional textiles, workwear and footwear, as well as business gifts – everything for a professional and recognizable image of your company. Browse our online catalogs.',
              it: 'Oltre alla stampa, offriamo anche tessuti promozionali, abbigliamento da lavoro e calzature, oltre a regali aziendali – tutto per un\'immagine professionale e riconoscibile della vostra azienda. Sfoglia i nostri cataloghi online.'
            })}
          </p>
        </Reveal>
        <Stagger className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8" stagger={0.15} data-testid="catalog-grid">
          {catalogs.map((catalog) => (
            <StaggerItem key={catalog.href} className="h-full">
              <CatalogCard catalog={catalog} />
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
};
